import { type FC, useState, type Dispatch, type SetStateAction } from 'react';
import { observer } from 'mobx-react';
import { Box, TextLink } from 'braid-design-system';
import JobSection from 'src/components/app/JobSection';
import UnhandledErrorPageBlock from '../../components/app/UnhandledErrorPageBlock';
import * as pageBlockStyles from '../../components/layouts/PageBlock/PageBlock.css';
import { UserLoadState } from '../../stores/UserStore/types';
import { getSignInUrl } from '../../pages/CandidateManagementPage/utils/getSignInUrl';
import PageAlert from './PageAlert';
import { useStores } from 'src/utils/hooks/useStores';
import CandidateList from 'src/components/list/index';
import { MobileFilterSection } from '../../components/app/MobileFilterSection';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { IntercomConfiguration } from 'src/modules/intercom/IntercomConfiguration';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { isANZSite } from 'src/utils/site';
import { UserActionStatusProvider } from 'src/components/common/UserActionStatusContext';
import { DEFAULT_ID } from 'src/constants/job';
import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../utils/getCsContactMethodMessageFromSite.ts';
import { BillableApplicantsDialog } from '../CandidateManagementPage/BillableApplicantsDialog/BillableApplicantDialog';
import { trackClickBillableApplicantsDefinition } from 'src/utils/events';
import { type PageTab, PageTabs } from '../CandidateManagementPage/PageTabs';
import loadable from 'sku/@loadable/component';
import { AppPageLayout } from './AppPageLayout';

const HirerJobActivity = loadable(
  () => import('src/components/app/HirerActivity/HirerJobActivity'),
);

interface AppPageBlockProps {
  isAtTopOfPage: boolean;
  selectedPageTab: PageTab;
  setSelectedPageTab: (tab: PageTab) => void;
  isStickyOnDesktop: boolean;
  setIsStickyOnDesktop: Dispatch<SetStateAction<boolean>>;
}

const AppPageBlock: FC<AppPageBlockProps> = ({
  isAtTopOfPage,
  isStickyOnDesktop,
  selectedPageTab,
  setIsStickyOnDesktop,
  setSelectedPageTab,
}) => {
  const { t } = useTranslations(translations);

  const store = useStores();
  const { jobApplicationStore, userStore, isMobile, jobStore, isError } = store;
  const { questionnaire: questionStore, job } = jobStore;

  // FIXME: this positive jobId check is here because we set jobId to -1 instead of null or undefined during static state. We don't want to use the jobApplicationStore.isLoading flag because that becomes true when we change buckets and refetch candidates
  const shouldShowRecommendedCandidates =
    job.isActive && jobApplicationStore.jobId > 0;

  const [isBillableApplicantsDialogOpen, setIsBillableApplicantsDialogOpen] =
    useState<boolean>(false);

  const openBillableAlertFromBanner = () => {
    trackClickBillableApplicantsDefinition({
      actionOrigin: 'Candidate Listing - Billable Alert',
    });
    setIsBillableApplicantsDialogOpen(true);
  };

  const { site, language } = useAppConfig();

  const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

  const noAccessToJobMessage = isANZSite(site)
    ? t(
        'If you think you should be one of them, ask your system administrator...',
        {
          TelLink: () => (
            <TextLink href={`tel:${csPhoneNumber}`}>{csPhoneNumber}</TextLink>
          ),
        },
      )
    : t(
        'If you think you should be one of them, ask your system administrator - with Customer Service link',
        {
          Link: (children) => (
            <TextLink href={getHelpCentreUrlFromSite(site, language)}>
              {children}
            </TextLink>
          ),
        },
      );

  const hidePageTabs =
    isMobile && (!shouldShowRecommendedCandidates || !isAtTopOfPage);

  if (userStore.userLoadState === UserLoadState.UNAUTHENTICATED) {
    return (
      <PageBlock>
        <PageAlert>
          {t('Sign in to see this page.', {
            Link: (children) => (
              <TextLink
                key={UserLoadState.UNAUTHENTICATED}
                href={getSignInUrl()}
              >
                {children}
              </TextLink>
            ),
          })}
        </PageAlert>
      </PageBlock>
    );
  }

  if (userStore.userLoadState === UserLoadState.FORBIDDEN) {
    return (
      <PageBlock>
        <PageAlert>
          {t(
            "This account can't access this page. Would you like to switch accounts?",
            {
              Link: (children) => (
                <TextLink key={UserLoadState.FORBIDDEN} href={getSignInUrl()}>
                  {children}
                </TextLink>
              ),
            },
          )}
        </PageAlert>
      </PageBlock>
    );
  }

  if (!jobStore.hasAccess) {
    return (
      <PageBlock>
        <PageAlert>
          {t('Only some people can see this page')}
          <br />
          {noAccessToJobMessage}
        </PageAlert>
      </PageBlock>
    );
  }

  if (isError || userStore.userLoadState === UserLoadState.ERROR) {
    return (
      <PageBlock>
        <Box marginY="small" marginX="gutter">
          <UnhandledErrorPageBlock />
        </Box>
      </PageBlock>
    );
  }

  const renderPageContent = () => {
    if (selectedPageTab === 'applications') {
      return (
        <CandidateList
          isStickyOnDesktop={isStickyOnDesktop}
          openBillableDialog={openBillableAlertFromBanner}
        />
      );
    }
    if (selectedPageTab === 'notes') {
      return <HirerJobActivity />;
    }
  };

  return (
    <UserActionStatusProvider
      jobId={store.jobId !== DEFAULT_ID ? `${store.jobId}` : ''}
      userId={store.userStore.user.userId || 0}
    >
      <IntercomConfiguration />
      <AppPageLayout
        jobSection={{
          boxShadow: hidePageTabs ? 'small' : undefined,
          element: (
            <JobSection
              job={jobStore.job}
              isRecommendedCandidatesTabShown={shouldShowRecommendedCandidates}
              openBillableDialog={() => {
                trackClickBillableApplicantsDefinition({
                  actionOrigin:
                    'Candidate Listing - Billable Applicants Button',
                });
                setIsBillableApplicantsDialogOpen(true);
              }}
            />
          ),
        }}
        pageTabs={
          !hidePageTabs && (
            <PageTabs
              jobId={jobApplicationStore.jobId}
              showRecommendedCandidates={shouldShowRecommendedCandidates}
              onApplicationsTabClick={() =>
                jobApplicationStore.loadJobApplications({
                  language,
                })
              }
              isStickyOnDesktop={isStickyOnDesktop}
              selectedPageTab={selectedPageTab}
              setIsStickyOnDesktop={setIsStickyOnDesktop}
              setSelectedPageTab={setSelectedPageTab}
            />
          )
        }
        mobileFilterSection={
          <MobileFilterSection jobApplicationStore={jobApplicationStore} />
        }
        content={renderPageContent()}
        billableApplicantsDialog={
          job.jobInformation.billableApplicants && (
            <BillableApplicantsDialog
              hasBillableApplicantsCountError={
                !job.jobInformation.billableApplicants.success
              }
              isOpen={isBillableApplicantsDialogOpen}
              mandatoryQuestions={questionStore.getMandatoryQuestions()}
              onClose={() => {
                setIsBillableApplicantsDialogOpen(false);
              }}
            />
          )
        }
      />
    </UserActionStatusProvider>
  );
};

const PageBlock: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box
    className={pageBlockStyles.pageBlock}
    display="flex"
    flexDirection="column"
    paddingY="none"
    paddingX={['none', 'xsmall']}
    marginY="none"
  >
    {children}
  </Box>
);

export default observer(AppPageBlock);
